import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, extractApiError, onUnauthenticated } from "./api";
import { DEFAULT_CAPABILITIES, normalizeCapabilities } from "./capabilities";

const AuthContext = createContext(null);
const IS_DEV = process.env.NODE_ENV !== "production";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [role, setRole] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [capabilities, setCapabilities] = useState(DEFAULT_CAPABILITIES);
  const [loading, setLoading] = useState(true);

  const applyMe = useCallback((data) => {
    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.debug("[auth] applyMe", { email: data?.user?.email, tenant: data?.tenant?.slug, role: data?.role });
    }
    setUser(data.user);
    setTenant(data.tenant);
    setRole(data.role);
    setTenants(data.tenants || []);
    setCapabilities(normalizeCapabilities(data.capabilities));
  }, []);

  const isSuperAdmin = Boolean(user?.is_super_admin || role === "SUPER_ADMIN");

  const clearAuthState = useCallback(() => {
    setUser(null);
    setTenant(null);
    setRole(null);
    setTenants([]);
    setCapabilities(DEFAULT_CAPABILITIES);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/api/auth/me");
      applyMe(r.data);
    } catch (e) {
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.debug("[auth] /me failed:", e?.response?.status);
      }
      clearAuthState();
    } finally {
      setLoading(false);
    }
  }, [applyMe, clearAuthState]);

  // Initial mount: probe current session
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-logout: api.js fires this when any authenticated endpoint returns 401.
  useEffect(() => {
    const unsubscribe = onUnauthenticated(() => {
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.debug("[auth] global 401 -> clearing auth state");
      }
      clearAuthState();
    });
    return unsubscribe;
  }, [clearAuthState]);

  const login = useCallback(async (email, password) => {
    try {
      const r = await api.post("/api/auth/login", { email, password });
      applyMe(r.data);
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.debug("[auth] login OK");
      }
      return { ok: true };
    } catch (e) {
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.debug("[auth] login failed:", e?.response?.status);
      }
      return { ok: false, error: extractApiError(e, "Anmeldung fehlgeschlagen.") };
    }
  }, [applyMe]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      clearAuthState();
    }
  }, [clearAuthState]);

  const switchTenant = useCallback(async (slug) => {
    try {
      const r = await api.post(`/api/auth/switch-tenant/${slug}`);
      applyMe(r.data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: extractApiError(e) };
    }
  }, [applyMe]);

  const value = useMemo(
    () => ({
      user,
      tenant,
      role,
      tenants,
      capabilities,
      loading,
      isSuperAdmin,
      login,
      logout,
      switchTenant,
      refresh,
    }),
    [user, tenant, role, tenants, capabilities, loading, isSuperAdmin, login, logout, switchTenant, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
